import * as VoxImplant from 'voximplant-websdk';
import CryptoJS from 'crypto-js';
import GibberishAES from 'gibberish-aes/src/gibberish-aes';

import { all, delay, put, select, take, takeEvery } from 'redux-saga/effects';

import { eventChannel } from 'redux-saga';

import { safeApply, safeCall } from './errors';

import { TITLE, TITLE_VOX } from '../../consts/pages';
import { VOX_ERRORS } from '../../consts/errors';
import { FETCH_AUTH_TOKEN } from '../actions/general';
import {
  mapVoxStatuses,
  VOX_AUTH,
  VOX_AUTH_SUCCESS,
  VOX_AUTH_FAILED,
  VOX_CONNECT,
  VOX_CONNECTED,
  VOX_CONNECTION_CLOSED,
  VOX_CONNECTION_ESTABLISHED,
  VOX_CONNECTION_FAILED,
  VOX_SHOW_ERROR,
  VOX_INIT,
  SET_VOX_STATUS,
  VOX_UNBAN,
  VOX_REQUEST_LAST_STATE,
  VOX_SYNC_LAST_STATE,
  VOX_DESTROY,
  VOX_SYNC_SOCKET_STATE,
  CHANGE_MANUAL_VOX_STATE,
  VOX_LOCALSTORAGE_LAST_STATE,
  VOX_LOCALSTORAGE_LAST_STATE_CHANGED_AT,
} from '../../consts/vox';

import { LOG_STATUS } from '../actions/log';
// eslint-disable-next-line import/no-cycle
import { onIncomingCall } from './dialer';

import fetchData from '../../api/fetchData';

import { VOX_STATES } from '@constants/vox';

export const vox = VoxImplant.getInstance();
window.vox = vox;

const getState = state => state.dialer;
const CHECK_CONNECTION_INTERVAL = 5 * 1000;

let watchers = [];

const shouldUseLocalStorageVoxStatus = (() => {
  const lastStatus = localStorage.getItem(VOX_LOCALSTORAGE_LAST_STATE);
  const lastUpdate = localStorage.getItem(
    VOX_LOCALSTORAGE_LAST_STATE_CHANGED_AT
  );

  if (!lastStatus || !lastUpdate) {
    return false;
  }

  const today = Date.now();

  const oneDayMs = 24 * 60 * 60 * 1000;
  const daysDiff = (today - lastUpdate) / oneDayMs;

  return daysDiff < 1;
})();

function* syncDialerState() {
  const { dialer, general } = yield select(state => state);
  const { isMasterInstance } = general;

  if (isMasterInstance) {
    yield put({
      dialer: { ...dialer },
      type: VOX_SYNC_LAST_STATE,
    });
  }
}

function* setVoxStatus({ isManual, operatorState }) {
  const { isMasterInstance, currentManager } = yield select(
    state => state.general
  );
  const lastStatus = localStorage.getItem(VOX_LOCALSTORAGE_LAST_STATE);
  const ACDStatus =
    mapVoxStatuses[operatorState] || VoxImplant.OperatorACDStatuses.Offline;

  if (!isMasterInstance) return;

  try {
    yield vox.setOperatorACDStatus(ACDStatus);
  } catch (message) {
    yield vox.getOperatorACDStatus();
  }

  if (isManual) {
    yield localStorage.setItem(VOX_LOCALSTORAGE_LAST_STATE, operatorState);
    yield localStorage.setItem(
      VOX_LOCALSTORAGE_LAST_STATE_CHANGED_AT,
      Date.now()
    );
  }

  if (lastStatus !== operatorState) {
    yield put({
      userId: Number(currentManager.id),
      status: operatorState,
      type: LOG_STATUS,
    });
  }
}

export function* setManualState({ payload }) {
  const { isManual, operatorState } = payload;
  const { manualState } = yield select(getState);

  yield put({
    isManual,
    operatorState: operatorState || manualState,
    type: SET_VOX_STATUS,
  });
}

export function* onMicAccessResult(event) {
  if (!event.result) {
    yield put({
      error: VOX_ERRORS.MIC,
      type: VOX_SHOW_ERROR,
    });
  }
}

function* onStatusUpdated({ status }) {
  yield put({
    state: VOX_STATES[status],
    operatorState: VOX_STATES[status],
    type: VOX_SYNC_SOCKET_STATE,
  });
}

function* checkSocketState() {
  yield all([safeCall(checkBannedState), safeCall(checkNeedManualState)]);
}

function* checkBannedState() {
  const stateData = yield select(getState);
  const { state, isUnbanning, forceUnban } = stateData;

  if (isUnbanning) {
    return;
  }

  if (forceUnban && state === VOX_STATES.BANNED) {
    yield put({ type: VOX_UNBAN });
  }
}

function* checkNeedManualState() {
  const stateData = yield select(getState);
  const { state, operatorState, manualState } = stateData;

  const statusToCheck = [
    VOX_STATES.READY,
    VOX_STATES.ONLINE,
    VOX_STATES.DND,
    VOX_STATES.OFFLINE,
  ].includes(operatorState);

  if (statusToCheck && state !== manualState) {
    yield safeCall(forceSetPreviousStatus);
  }
}

export function loginVox(number, password) {
  const callcenterUrl = process.env.URL_CALLCENTER;

  return vox.login(`${number}@${callcenterUrl}`, password);
}

export function decryptData(data) {
  const key = CryptoJS.SHA1(process.env.SHA_KEY).toString();
  const decryptedData = GibberishAES.dec(data, key);
  return JSON.parse(decryptedData);
}

function* connectVox() {
  try {
    yield vox.connect();
  } catch (error) {
    yield delay(CHECK_CONNECTION_INTERVAL);
    if (vox.getClientState() === 'DISCONNECTED') {
      yield put({ type: VOX_CONNECT });
    }
  }
}

function* forceSetPreviousStatus() {
  if (shouldUseLocalStorageVoxStatus) {
    const localStorageStatus = localStorage.getItem(
      VOX_LOCALSTORAGE_LAST_STATE
    );

    yield put({
      isManual: true,
      operatorState: localStorageStatus,
      type: SET_VOX_STATUS,
    });
  }
}

function* authVox() {
  const res = yield safeCall(
    fetchData,
    `${process.env.API_HOST_URL}${process.env.API_AUTH}`
  );

  const {
    vox_name: number,
    vox_password: password,
    vox_supervisor: isSupervisor,
  } = yield safeCall(decryptData, res?.data || process.env.VOX_ENCTYPTED_DATA);

  const { displayName: name } = yield safeCall(loginVox, number, password);

  if (!name) return;

  yield put({
    operator: {
      name,
      number,
    },
    isSupervisor,
    type: VOX_CONNECTED,
  });
}

function* configHardware() {
  yield VoxImplant.Hardware.AudioDeviceManager.get().setDefaultAudioSettings({
    echoCancellation: false,
    noiseSuppression: false,
  });
}

function* onReady() {
  yield safeCall(configHardware);
  yield put({ type: VOX_CONNECT });
}

export function createChannel(event, instance) {
  return eventChannel(emit => {
    const successHandler = res => emit(res);
    instance.on(event, successHandler);

    const unsubscribe = () => instance.off(event);
    return unsubscribe;
  });
}

function* stopWatchers() {
  yield watchers.forEach(channel => channel.close());
}

function* watchSDKReady() {
  const { SDKReady } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, SDKReady, vox);
  watchers = [...watchers, channel];

  while (true) {
    try {
      yield take(channel);
      yield safeCall(onReady);
    } catch (message) {
      yield put({
        message,
        error: VOX_ERRORS.SDK,
        type: VOX_SHOW_ERROR,
      });
    }
  }
}

function* watchMicAccessResult() {
  const { MicAccessResult } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, MicAccessResult, vox);
  watchers = [...watchers, channel];

  while (true) {
    try {
      const payload = yield take(channel);
      yield safeCall(onMicAccessResult, payload);
    } catch (error) {
      yield put({
        error: VOX_ERRORS.MIC,
        type: VOX_SHOW_ERROR,
      });
    }
  }
}

function* watchAuthResult() {
  const { AuthResult } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, AuthResult, vox);
  watchers = [...watchers, channel];

  while (true) {
    try {
      const { result } = yield take(channel);

      if (result) {
        yield put({ type: VOX_AUTH_SUCCESS, vox });
      }
    } catch (message) {
      yield put({ type: VOX_AUTH_FAILED });
      yield delay(CHECK_CONNECTION_INTERVAL);
      yield put({ type: FETCH_AUTH_TOKEN });
      yield put({ type: VOX_AUTH });
    }
  }
}

function* watchConnectionEstablished() {
  const { ConnectionEstablished } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, ConnectionEstablished, vox);
  watchers = [...watchers, channel];

  while (true) {
    try {
      yield take(channel);
      yield put({ type: VOX_CONNECTION_ESTABLISHED });
      yield put({ type: VOX_AUTH });
    } catch (message) {
      yield put({
        message,
        error: VOX_ERRORS.AUTH,
        type: VOX_SHOW_ERROR,
      });
    }
  }
}

function* watchConnectionFailed() {
  // Connection couldn't be established.
  const { ConnectionFailed } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, ConnectionFailed, vox);
  watchers = [...watchers, channel];

  while (true) {
    yield take(channel);
    yield put({ type: VOX_CONNECTION_FAILED });
    yield delay(CHECK_CONNECTION_INTERVAL);
    yield put({ type: VOX_CONNECT });
  }
}

function* watchConnectionClosed() {
  // Because of network problems
  const { ConnectionClosed } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, ConnectionClosed, vox);
  watchers = [...watchers, channel];

  while (true) {
    yield take(channel);
    yield put({ type: VOX_CONNECTION_CLOSED });
    yield delay(CHECK_CONNECTION_INTERVAL);
    yield put({ type: VOX_CONNECT });
  }
}

function* watchIncomingCall() {
  const { IncomingCall } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, IncomingCall, vox);
  watchers = [...watchers, channel];

  while (true) {
    const payload = yield take(channel);
    yield safeCall(onIncomingCall, payload);
  }
}

function* watchACDStatusUpdated() {
  const { ACDStatusUpdated } = VoxImplant.Events;
  const channel = yield safeCall(createChannel, ACDStatusUpdated, vox);
  watchers = [...watchers, channel];

  while (true) {
    try {
      const payload = yield take(channel);
      yield safeCall(onStatusUpdated, payload);
    } catch (err) {
      // ACD STATUS ERROR
    }
  }
}

async function getMicPermission() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    if (
      error.name === 'NotAllowedError' ||
      error.name === 'PermissionDeniedError'
    ) {
      return false;
    }
  }
}

async function getNotificationsPermission() {
  const res = await navigator.permissions.query({ name: 'notifications' });
  return res.state !== 'denied';
}

function requestMicPermission() {
  try {
    navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (error) {}
}

function requestPermissions({ error }) {
  if (error === VOX_ERRORS.NOTIFICATIONS) {
    Notification.requestPermission();
  }
  if (error === VOX_ERRORS.MIC) {
    requestMicPermission();
  }
}

function* initVox() {
  const isMicReady = yield safeCall(getMicPermission);
  const isNotificationsReady = yield safeCall(getNotificationsPermission);

  if (!isMicReady) {
    yield put({
      error: VOX_ERRORS.MIC,
      type: VOX_SHOW_ERROR,
    });
    return;
  }

  if (!isNotificationsReady) {
    yield put({
      error: VOX_ERRORS.NOTIFICATIONS,
      type: VOX_SHOW_ERROR,
    });
    return;
  }

  vox.init({
    node: VoxImplant.ConnectionNode.NODE_4,
  });

  document.title = TITLE_VOX;

  yield all([
    safeCall(watchSDKReady),
    safeCall(watchMicAccessResult),
    safeCall(watchConnectionEstablished),
    safeCall(watchConnectionFailed),
    safeCall(watchConnectionClosed),
    safeCall(watchIncomingCall),
    safeCall(watchACDStatusUpdated),
    safeCall(watchAuthResult),
  ]);
}

function* destroyVox() {
  document.title = TITLE;

  yield safeCall(stopWatchers);
}

export default function* () {
  const { currentManager } = yield select(state => state.general);

  if (currentManager?.isOperator) {
    yield all([
      yield takeEvery(VOX_INIT, safeApply(initVox)),
      yield takeEvery(VOX_AUTH, safeApply(authVox)),
      yield takeEvery(VOX_AUTH_SUCCESS, safeApply(forceSetPreviousStatus)),
      yield takeEvery(VOX_CONNECT, safeApply(connectVox)),
      yield takeEvery(SET_VOX_STATUS, safeApply(setVoxStatus)),
      yield takeEvery(VOX_REQUEST_LAST_STATE, safeApply(syncDialerState)),
      yield takeEvery(VOX_DESTROY, safeApply(destroyVox)),
      yield takeEvery(CHANGE_MANUAL_VOX_STATE, safeApply(setManualState)),
      yield takeEvery(VOX_SYNC_SOCKET_STATE, safeApply(checkSocketState)),
      yield takeEvery(VOX_SHOW_ERROR, safeApply(requestPermissions)),
    ]);
  }
}
